import { logger } from '@luna/logger';
import { loadTranslationNamespace } from '@luna/i18n';

import type { Plugin } from './types';
import { getDirectoryByPluginId } from './PluginDirectoryMapping';
import type { PluginModule } from './registerPluginModule';
import { getPluginModule } from './registerPluginModule';

export function hasActiveFlag(plugin: Plugin): boolean {
    return plugin.flags?.includes('active') ?? false;
}

export const preparePluginFlagsForActivation = (plugin?: Plugin): Plugin['flags'] => {
    if (!plugin || !plugin.flags) {
        return ['active'];
    }
    if (plugin.flags.includes('active')) {
        return plugin.flags;
    }
    return [...plugin.flags, 'active'];
};

export const preparePluginFlagsForDeactivation = (plugin?: Plugin): Plugin['flags'] | undefined => {
    if (!plugin || !plugin.flags) {
        return undefined;
    }

    return plugin.flags.filter((f) => f !== 'active');
};

const hasDirectory = (plugin: Plugin): boolean => !!getDirectoryByPluginId(plugin.productCode);

export const selectActivePlugins = (plugins: Plugin[]): Plugin[] =>
    plugins.filter(hasActiveFlag).filter(hasDirectory);

function loadPluginModule(plugin: Plugin): PluginModule | undefined {
    const pluginModule = getPluginModule(plugin.productCode);

    if (!pluginModule) {
        logger.error('Plugins :: failed to load plugin module ', plugin.productCode);
        return undefined;
    }

    pluginModule.onPluginLoad?.();

    return pluginModule;
}

export async function loadPlugin(plugin: Plugin): Promise<PluginModule | undefined> {
    await loadTranslationNamespace('plugins');

    return loadPluginModule(plugin);
}

export async function loadActivePlugins(plugins: Plugin[]): Promise<void> {
    const loadablePlugins = plugins.filter(hasDirectory).filter(hasActiveFlag);
    const loadPlugins = loadablePlugins.map(loadPlugin);

    await Promise.all(loadPlugins);
}
